import {useResizeObserver} from "usehooks-ts";
import {Dispatch, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef} from "react";
import {Vec2} from "./math/vec2";
import {clamp} from "./math/misc";
import {Mat3} from "./math/mat3";
import {smoothNudge} from "./math/lerp";
import {calculateDiagramBounds} from "./diagramBounds";
import {ClientDiagram, isDiagramEmpty} from "./types";

export class Camera {
  position: Vec2;
  size: Vec2;
  targetPosition: Vec2 | null;

  zoomStep: number;
  #targetZoomStep: number;

  get zoomFactor(): number {
    return Math.pow(1.1, this.zoomStep);
  }

  get targetZoomFactor(): number {
    return Math.pow(1.1, this.targetZoomStep);
  }

  get targetZoomStep(): number {
    return this.#targetZoomStep;
  }

  set targetZoomStep(zoom: number) {
    this.#targetZoomStep = clamp(zoom, -8, 8);
  }

  static default(): Camera {
    return new Camera(Vec2.zero(), Vec2.zero(), 1.0);
  }

  static forExport(size: Vec2, center: Vec2): Camera {
    return new Camera(size, center, 0);
  }

  private constructor(
    size: Vec2,
    initialPosition: Vec2,
    initialTargetZoom: number,
    initialZoom?: number,
    targetPosition?: Vec2,
  ) {
    this.size = size.clone();
    this.position = initialPosition.clone();
    this.#targetZoomStep = initialTargetZoom;
    this.zoomStep = initialZoom ?? initialTargetZoom;
    this.targetPosition = targetPosition ?? null;
  }

  clone(): Camera {
    return new Camera(this.size, this.position, this.targetZoomStep, this.zoomStep, this.targetPosition ?? undefined);
  }

  /** Map screen coordinates to world coordinates. */
  s2w(screenPos: Vec2): Vec2 {
    return this.viewMatrix().inverse().transformPoint(screenPos);
  }

  /** Map world coordinates to screen coordinates. */
  w2s(worldPos: Vec2): Vec2 {
    return this.viewMatrix().transformPoint(worldPos);
  }

  /** Get the view matrix for the camera. */
  viewMatrix(): Mat3 {
    const centerOffset = Mat3.fromTranslation(this.size.divScalar(2));
    const translation = Mat3.fromTranslation(this.position.negate());
    const zoom = Mat3.fromScale(Vec2.splat(this.zoomFactor));

    return centerOffset.mul(zoom.mul(translation));
  }
}

/** This hook ensures that the camera's size is updated when the parent element is resized. */
export function useResizeCamera(parentRef: RefObject<HTMLElement>, setCamera: Dispatch<SetStateAction<Camera>>) {
  useResizeObserver(
    useMemo(
      (): Parameters<typeof useResizeObserver>[0] => ({
        ref: parentRef,
        onResize: size => {
          setCamera(prev => {
            if (size.width == null || size.height == null) {
              return prev;
            }

            const next = prev.clone();
            next.size = new Vec2(size.width, size.height);
            return next;
          });
        },
      }),
      [parentRef, setCamera],
    ),
  );
}

export function useSmoothlyUpdateCameraZoom(setCamera: Dispatch<SetStateAction<Camera>>) {
  useEffect(() => {
    let animationId: number;
    let lastTime = performance.now();

    const tick = (time: number) => {
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      setCamera(prev => {
        if (prev.zoomStep === prev.targetZoomStep) {
          return prev;
        }

        const next = prev.clone();
        next.zoomStep = smoothNudge(prev.zoomStep, prev.targetZoomStep, 16, dt, 1e-4);
        return next;
      });

      animationId = requestAnimationFrame(tick);
    };

    animationId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationId);
  }, [setCamera]);
}

export function useSmoothlyUpdateCameraPosition(setCamera: Dispatch<SetStateAction<Camera>>) {
  useEffect(() => {
    let animationId: number;
    let lastTime = performance.now();

    const tick = (time: number) => {
      const dt = (time - lastTime) / 1000;
      lastTime = time;

      setCamera(prev => {
        if (prev.targetPosition == null || prev.position === prev.targetPosition) {
          return prev;
        }

        const next = prev.clone();
        next.position = next.position.smoothNudge(next.targetPosition!, 16, dt, 1e-4);

        if (next.position.equals(next.targetPosition!)) {
          next.targetPosition = null;
        }

        return next;
      });

      animationId = requestAnimationFrame(tick);
    };

    animationId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationId);
  }, [setCamera]);
}

/** Initialize camera to center and zoom to fit the diagram bounds on initial load. */
export function useInitializeCameraView(
  camera: Camera,
  setCamera: Dispatch<SetStateAction<Camera>>,
  diagram: ClientDiagram,
) {
  const initialized = useRef<boolean>(false);

  const initializeCameraView = useCallback(() => {
    if (initialized.current) {
      return;
    }

    setCamera(prev => {
      // Only update if viewport size is known
      if (prev.size.x === 0 || prev.size.y === 0) {
        return prev;
      }

      initialized.current = true;

      const next = prev.clone();

      // If diagram is empty, start at (0, 0) with 100% zoom
      if (isDiagramEmpty(diagram)) {
        next.targetZoomStep = 0;
        next.targetPosition = Vec2.zero();
        return next;
      }

      // Otherwise, center and zoom to fit the diagram
      const bounds = calculateDiagramBounds(diagram, 1);
      const boundsCenter = bounds.center;
      const boundsSize = bounds.size;

      const zoomX = prev.size.x / boundsSize.x;
      const zoomY = prev.size.y / boundsSize.y;
      const targetZoom = Math.min(zoomX, zoomY);

      next.zoomStep = Math.floor(Math.log(targetZoom) / Math.log(1.1));
      next.targetZoomStep = next.zoomStep;

      next.position = boundsCenter;
      next.targetPosition = null;

      return next;
    });
  }, [diagram, setCamera]);

  // Call initialization when camera size becomes available
  useEffect(() => {
    initializeCameraView();
  }, [camera.size, initializeCameraView]);
}
